import PageLayout from "@/components/layout/PageLayout";
import TradingResizableSection from "@/components/trading/TradingResizableSection";
import { ChartOptionsProvider } from "@/lib/tradingview/chart-options-context";
import { translateBrand } from "@/lib/i18n";
import { BrandMessageIds } from "@nitro-ape/shared/lib/brand-config";
import { MonacoCoinProvider } from "@nitro-ape/shared/features/monaco/api/providers/monaco-coin-provider";
import { parseMonacoSymbolFromUrl } from "@nitro-ape/shared/features/monaco/api/utils";
import MonacoOrdersListener from "@nitro-ape/shared/features/monaco/api/listeners/monaco-orders-listener";
import MonacoTradesListener from "@nitro-ape/shared/features/monaco/api/listeners/monaco-trades-listener";
import {
  MonacoPerpOrderbookListener,
  MonacoPerpOrdersListener,
  MonacoPerpsMarginAccountProvider,
  useMonacoPerpsMarginAccount,
} from "@nitro-ape/shared/features/monaco-perps/api";
import { PoolIdProvider } from "@nitro-ape/shared/providers/pool-id-provider";
import { Chain } from "@nitro-ape/shared/models/enums/chain";
import { Spinner } from "@heroui/spinner";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Suspense } from "react";

import { LimitCloseModalProvider } from "@/components/trading/modals/providers/limit-close-modal-provider";
import { MarketCloseModalProvider } from "@/components/trading/modals/providers/market-close-modal-provider";
import PerpsChainSelector from "@/components/trading/PerpsChainSelector";
import MonacoPerpsSidebar from "@/components/trading/Sidebar/MonacoPerpsSidebar";
import { TradeChainProvider } from "@/providers/trade-chain-context";
import { PositionMarginModalProvider } from "../perpetuals/-providers/position-margin-modal-provider";
import { PositionTPSLModalProvider } from "../perpetuals/-providers/position-tpsl-modal-provider";
import RecentMonacoPerpListener from "./-components/listeners/RecentMonacoPerpListener";

export const Route = createFileRoute("/_main/monaco-perpetuals/$coin")({
  head: () => ({
    meta: [
      {
        title: translateBrand(BrandMessageIds.metaTitlePerpetuals),
      },
    ],
  }),
  component: MonacoPerpetualsCoinPage,
});

function MonacoPerpetualsCoinPage() {
  const { coin } = useParams({ from: "/_main/monaco-perpetuals/$coin" });
  const parsedSymbol = parseMonacoSymbolFromUrl(coin);

  return (
    <PageLayout>
      <Suspense
        fallback={
          <div className="flex h-screen items-center justify-center">
            <Spinner size="lg" color="secondary" />
          </div>
        }
      >
        <ChartOptionsProvider>
          <PoolIdProvider poolId={coin} chain={Chain.monaco_perps}>
            <TradeChainProvider chain={Chain.monaco_perps}>
            <MonacoCoinProvider chain={Chain.monaco_perps} symbol={parsedSymbol}>
              <MonacoPerpsMarginAccountProvider>
                <MonacoPerpsListeners>
                  <RecentMonacoPerpListener coin={coin}>
                    <PositionTPSLModalProvider>
                      <PositionMarginModalProvider>
                        <MarketCloseModalProvider>
                          <LimitCloseModalProvider>
                            <div className="flex h-full max-h-full w-full flex-col gap-1.5">
                              <PerpsChainSelector
                                selected={Chain.monaco_perps}
                              />
                              <div className="flex h-full max-h-full w-full min-h-0 gap-1.5">
                                <TradingResizableSection />
                                {/* Desktop-only sidebar — mobile uses the
                                    bottom-sheet order form built into
                                    `TradeMobileLayout`. */}
                                <div className="hidden md:block">
                                  <MonacoPerpsSidebar />
                                </div>
                              </div>
                            </div>
                          </LimitCloseModalProvider>
                        </MarketCloseModalProvider>
                      </PositionMarginModalProvider>
                    </PositionTPSLModalProvider>
                  </RecentMonacoPerpListener>
                </MonacoPerpsListeners>
              </MonacoPerpsMarginAccountProvider>
            </MonacoCoinProvider>
            </TradeChainProvider>
          </PoolIdProvider>
        </ChartOptionsProvider>
      </Suspense>
    </PageLayout>
  );
}

function MonacoPerpsListeners({ children }: { children: React.ReactNode }) {
  // Movement listeners (spot + perps) are mounted globally in
  // _main/-providers.tsx so balance changes propagate on every authenticated
  // route (wallet, dashboard, etc.). Only pair-scoped listeners that depend
  // on the currently selected coin live here.
  const { marginAccountId } = useMonacoPerpsMarginAccount();
  return (
    <MonacoPerpOrderbookListener>
      <MonacoTradesListener>
        <MonacoOrdersListener>
          <MonacoPerpOrdersListener marginAccountId={marginAccountId}>
            {children}
          </MonacoPerpOrdersListener>
        </MonacoOrdersListener>
      </MonacoTradesListener>
    </MonacoPerpOrderbookListener>
  );
}
